<!-- Step 1: Dialog — Dispatch type + Claim number -->
<p-dialog
  header="Create Dispatch"
  [(visible)]="visible"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: 'min(640px, 95vw)' }"
  styleClass="create-dispatch-dialog"
  (onHide)="onDialogHide()">

  <div class="flex items-center p-1 bg-[#f5faff] border border-[#d3e5f9] rounded-lg mb-3">
    @for (option of dispatchTypes; track option.value) {
      <button
        type="button"
        class="flex-1 py-2 sm:py-2.5 px-2 sm:px-5 rounded-lg text-xs sm:text-base text-center cursor-pointer border-0 transition-colors"
        [class.bg-[#0078d6]]="dispatchType() === option.value"
        [class.text-white]="dispatchType() === option.value"
        (click)="dispatchType.set(option.value)">
        {{ option.label }}
      </button>
    }
  </div>

  <p class="text-base leading-[26px] mb-3">
    @switch (dispatchType()) {
      @case ('new') {
        To create a <strong class="font-semibold">New</strong> dispatch, enter a claim number.
      }
      @case ('manual') {
        To create a <strong class="font-semibold">Manual</strong> dispatch, enter a claim number and select a template.
      }
      @case ('partial') {
        To create a <strong class="font-semibold">Partial Manual</strong> dispatch, enter a claim number.
      }
    }
  </p>

  <input
    pInputText
    class="w-full"
    placeholder="Claim Number"
    [ngModel]="claimNumber()"
    (ngModelChange)="claimNumber.set($event)" />

  <!-- Template selector — Manual Dispatch only -->
  @if (dispatchType() === 'manual') {
    <div class="mt-3">
      <p-select
        class="w-full"
        styleClass="w-full"
        [options]="templates()"
        [ngModel]="selectedTemplate()"
        (ngModelChange)="selectedTemplate.set($event)"
        optionLabel="templateName"
        placeholder="Select Template"
        [loading]="templatesLoading()"
        appendTo="body"
        [panelStyle]="{ 'min-width': '100%' }">

        <!-- Column header inside the dropdown panel -->
        <ng-template pTemplate="header">
          <div class="hidden sm:flex px-3 pt-2 pb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200 bg-white">
            <span class="flex-1 min-w-0">Template Name</span>
            <span class="w-44 shrink-0">Company</span>
            <span class="w-28 shrink-0">LOB</span>
          </div>
        </ng-template>

        <!-- Each template row in the dropdown panel -->
        <ng-template pTemplate="item" let-tmpl>
          <!-- Desktop: row layout -->
          <div class="hidden sm:flex w-full gap-2">
            <span class="flex-1 min-w-0 truncate">{{ tmpl.templateName }}</span>
            <span class="w-44 shrink-0 text-gray-700">
              {{ tmpl.companyCd | codeDescription:'company' | async }}
            </span>
            <span class="w-28 shrink-0 text-gray-700">
              {{ tmpl.lobCd | codeDescription:'lob' | async }}
            </span>
          </div>
          <!-- Mobile: stacked layout -->
          <div class="sm:hidden">
            <span class="block truncate">{{ tmpl.templateName }}</span>
            <span class="block text-xs text-gray-500 mt-0.5">
              {{ tmpl.companyCd | codeDescription:'company' | async }}
            </span>
          </div>
        </ng-template>

        <!-- Empty state -->
        <ng-template pTemplate="empty">
          <span class="text-sm text-gray-500 px-3 py-2 block">
            @if (templatesLoading()) { Loading templates… } @else { No templates available. }
          </span>
        </ng-template>
      </p-select>

      @if (templatesLoadError()) {
        <p class="text-red-600 text-xs mt-1">{{ templatesLoadError() }}</p>
      }
    </div>
  }

  @if (error()) {
    <p class="text-red-600 text-sm mt-2">{{ error() }}</p>
  }

  <ng-template #footer>
    <p-button label="Cancel" [text]="true" (onClick)="onCancel()" />
    <p-button label="Continue" [disabled]="!canContinueDialog() || loading()" [loading]="loading()" (onClick)="onDialogContinue()" />
  </ng-template>
</p-dialog>

<!-- Step 1.5: Duplicate Dispatch Modal -->
<!-- appendTo body so it stacks above the still-open reserve drawer (drawer is body-appended). -->
<p-dialog
  header="Duplicate Dispatch Found"
  [(visible)]="duplicateModalVisible"
  appendTo="body"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: 'min(760px, 95vw)' }"
  styleClass="duplicate-dispatch-dialog"
  (onHide)="onDuplicateModalCancel()">

  <p class="text-sm leading-6 mb-4">
    There are existing claim(s) that closely match the details below. If there's reason to proceed, please select the estimate type.
  </p>

  @if (duplicateDispatches()[0]; as first) {
    <!-- Claim/unit context bar -->
    <div class="grid grid-cols-2 sm:grid-cols-3 md:flex gap-3 md:gap-4 bg-[#f8f8fa] border border-[#e0e0e0] rounded p-3 mb-3 text-sm">
      <div class="shrink-0">
        <p class="text-xs text-[--text-secondary] m-0">Claim Number</p>
        <p class="m-0 font-medium">{{ first.claimNumber }}</p>
      </div>
      <div class="min-w-0 flex-1">
        <p class="text-xs text-[--text-secondary] m-0">Unit</p>
        <p class="m-0 font-medium truncate" [title]="first.unit">{{ first.unit }}</p>
      </div>
      <div class="shrink-0">
        <p class="text-xs text-[--text-secondary] m-0">Coverage</p>
        <p class="m-0 font-medium">{{ first.coverage }}</p>
      </div>
      <div class="shrink-0">
        <p class="text-xs text-[--text-secondary] m-0">Loss Type</p>
        <p class="m-0 font-medium">{{ first.lossType }}</p>
      </div>
      <div class="shrink-0">
        <p class="text-xs text-[--text-secondary] m-0">Adjuster</p>
        <p class="m-0 font-medium">{{ first.adjuster }}</p>
      </div>
    </div>

    <!-- Existing dispatches table -->
    <p-table
      [value]="duplicateDispatches()"
      [scrollable]="true"
      scrollHeight="180px"
      styleClass="w-full mb-4">
      <ng-template #header>
        <tr>
          <th class="whitespace-nowrap">ClaimID</th>
          <th class="whitespace-nowrap">Status</th>
          <th class="whitespace-nowrap">Estimate Type</th>
          <th class="whitespace-nowrap">Date Assigned</th>
          <th class="whitespace-nowrap">Appraiser</th>
        </tr>
      </ng-template>
      <ng-template #body let-row>
        <tr>
          <td class="whitespace-nowrap">{{ row.claimId }}</td>
          <td class="whitespace-nowrap">{{ (row.status | codeDescription:'status') | async | titlecase }}</td>
          <td class="whitespace-nowrap">{{ row.seqType }}</td>
          <td class="whitespace-nowrap">{{ row.assignmentDate }}</td>
          <td class="whitespace-nowrap">{{ row.appraiserName }}</td>
        </tr>
      </ng-template>
    </p-table>
  }

  <!-- Estimate Type dropdown -->
  <p-select
    class="w-full"
    styleClass="w-full"
    [options]="estimateTypeOptions"
    optionLabel="label"
    optionValue="value"
    appendTo="body"
    placeholder="Estimate Type"
    [ngModel]="duplicateEstimateType()"
    (ngModelChange)="duplicateEstimateType.set($event)" />
  @if (duplicateModalSubmitted() && !duplicateEstimateType()) {
    <p class="text-red-600 text-xs mt-1">Estimate type is required.</p>
  }

  <ng-template #footer>
    <p-button label="Cancel" [text]="true" (onClick)="onDuplicateModalCancel()" />
    <p-button label="Continue with New Dispatch" (onClick)="onContinueWithNewDispatch()" />
  </ng-template>
</p-dialog>

<!-- Step 2: Drawer — Claim number + Reserve selection -->
<p-drawer
  [(visible)]="drawerVisible"
  position="right"
  [style]="{ width: 'min(650px, 100vw)' }"
  styleClass="create-dispatch-drawer"
  (onHide)="onDrawerCancel()">

  <ng-template #header>
    <h2 class="text-2xl font-normal m-0">{{ drawerTitle() }}</h2>
  </ng-template>

  <div class="drawer-form-container">
    <p-floatLabel class="w-full">
      <input pInputText class="w-full" id="drawerClaimNum" [value]="claimNumber()" readonly />
      <label for="drawerClaimNum">Claim Number</label>
    </p-floatLabel>

    <div class="drawer-form-body mt-5">
      <p class="font-semibold text-base m-0">Select a Reserve</p>

      @for (reserve of reserves(); track reserve.unitAsgnmntId) {
        <div
          class="reserve-card border rounded-lg p-4 pl-2 cursor-pointer transition-colors"
          [class.border-[#0078d6]]="selectedReserveId() === reserve.unitAsgnmntId"
          [class.bg-[#eff6fd]]="selectedReserveId() === reserve.unitAsgnmntId"
          [class.border-[#bec7d1]]="selectedReserveId() !== reserve.unitAsgnmntId"
          (click)="selectedReserveId.set(reserve.unitAsgnmntId)">
          <div class="flex items-center gap-2.5">
            <p-radioButton
              name="reserveSelection"
              [value]="reserve.unitAsgnmntId"
              [ngModel]="selectedReserveId()"
              (ngModelChange)="selectedReserveId.set($event)" />
            <div class="flex-1">
              <span class="text-xs text-[--text-secondary]">Unit</span>
              <p class="text-sm m-0">{{ reserve.unit }}</p>
            </div>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-2 ml-[42px]">
            <div>
              <span class="text-xs text-[--text-secondary]">Coverage</span>
              <p class="text-sm m-0">{{ reserve.coverage || '–' }}</p>
            </div>
            <div>
              <span class="text-xs text-[--text-secondary]">Loss Type</span>
              <p class="text-sm m-0">{{ reserve.lossType || '–' }}</p>
            </div>
            <div>
              <span class="text-xs text-[--text-secondary]">Rep</span>
              <p class="text-sm m-0">{{ reserve.rep || '–' }}</p>
            </div>
          </div>
        </div>
      }
    </div>

    @if (error() && drawerVisible()) {
      <p class="text-red-600 text-sm mt-2">{{ error() }}</p>
    }

    <div class="drawer-form-footer">
      <p-button label="Cancel" [text]="true" (onClick)="onDrawerCancel()" />
      <p-button label="Continue" [disabled]="!canContinueDrawer() || loading()" [loading]="loading()" (onClick)="onDrawerContinue()" />
    </div>
  </div>
</p-drawer>

<!-- Step 2.5: Unit Edit Dialog — shown when required fields are missing/invalid -->
<app-unit-edit-dialog
  [(visible)]="unitEditVisible"
  [fieldSpecs]="missingFieldSpecs()"
  [unitData]="currentUnitRawData()"
  [saving]="savingUnitEdit()"
  (saved)="onUnitEditSaved($event)"
  (cancelled)="onUnitEditCancelled()" />

<!-- Step 3: Select Appraiser (shared component — also used by Transfer Assignment) -->
<app-appraiser-select-dialog
  [visible]="appraiserDialogVisible()"
  (visibleChange)="onAppraiserDialogVisibleChange($event)"
  dialogHeader="Select Appraiser"
  [claimNumber]="claimNumber()"
  [unitName]="selectedReserve()?.unit ?? '–'"
  currentAppraiser="–"
  [initialAddress]="inspectionAddress()"
  [baseParams]="appraiserBaseParams()"
  submitLabel="Create Dispatch"
  [submitting]="creatingDispatch()"
  [declineOptions]="declineOptions()"
  (appraiserSelected)="onAppraiserSelected($event)"
  (cancelled)="onAppraiserDialogCancel()">
</app-appraiser-select-dialog>

<!-- Actions Summary Dialog -->
<app-actions-summary-dialog
  [(visible)]="showActionsSummaryDialog"
  [data]="actionsSummaryData()"
  [infoMessage]="addressSyncMessage()"
  buttonLabel="Continue"
  (dismissed)="closeActionsSummary()" />
